import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Combobox } from "@/components/ui/combobox";
import type { TourRecord, TourStepRecord } from "@/components/admin-tours/types";

interface ToursResponse {
  tours: TourRecord[];
}
interface StepsResponse {
  steps: TourStepRecord[];
}

type ProbeStatus = "pending" | "probing" | "found" | "missing" | "unsupported";

interface ProbeResultMessage {
  type: "tour-preview:probe-result";
  requestId: string;
  results: { stepId: string; status: "found" | "missing" }[];
}

const PROBE_TIMEOUT_MS = 5000;
const POST_LOAD_SETTLE_MS = 500;

function isPreviewableRoute(routePattern: string): boolean {
  const trimmed = routePattern.trim();
  if (!trimmed.startsWith("/")) return false;
  if (trimmed.includes(":") || trimmed.includes("*")) return false;
  return true;
}

function buildAuditUrl(routePattern: string): string {
  const params = new URLSearchParams({ previewFor: "tour-step" });
  return `${routePattern.trim()}?${params.toString()}`;
}

function describeStepTarget(step: TourStepRecord): string {
  if (step.targetId) return `data-tour="${step.targetId}"`;
  if (step.targetSelector) return step.targetSelector;
  return "(no target)";
}

function StatusBadge({ status }: { status: ProbeStatus }) {
  if (status === "found") {
    return (
      <Badge variant="default" className="gap-1" data-testid="badge-step-found">
        <CheckCircle2 className="h-3 w-3" /> Found
      </Badge>
    );
  }
  if (status === "missing") {
    return (
      <Badge variant="destructive" className="gap-1" data-testid="badge-step-missing">
        <AlertTriangle className="h-3 w-3" /> Missing
      </Badge>
    );
  }
  if (status === "probing") {
    return (
      <Badge variant="secondary" className="gap-1" data-testid="badge-step-probing">
        <Loader2 className="h-3 w-3 animate-spin" /> Probing
      </Badge>
    );
  }
  if (status === "unsupported") {
    return (
      <Badge variant="outline" className="gap-1" data-testid="badge-step-unsupported">
        <AlertTriangle className="h-3 w-3" /> Skipped
      </Badge>
    );
  }
  return (
    <Badge variant="outline" data-testid="badge-step-pending">
      Pending
    </Badge>
  );
}

export default function AdminToursAuditPage() {
  const toursQuery = useQuery<ToursResponse>({ queryKey: ["/api/admin/tours"] });
  const tours = useMemo(() => toursQuery.data?.tours ?? [], [toursQuery.data]);

  const [selectedTourId, setSelectedTourId] = useState<string>("");
  const selectedTour = tours.find((t) => t.id === selectedTourId) ?? null;

  const stepsQuery = useQuery<StepsResponse>({
    queryKey: ["/api/admin/tours", selectedTourId, "steps"],
    enabled: !!selectedTourId,
  });
  const steps = useMemo(() => stepsQuery.data?.steps ?? [], [stepsQuery.data]);

  const previewable = !!selectedTour && isPreviewableRoute(selectedTour.routePattern);
  const auditUrl = selectedTour && previewable ? buildAuditUrl(selectedTour.routePattern) : null;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [iframeReady, setIframeReady] = useState(false);
  const [probeMap, setProbeMap] = useState<Record<string, ProbeStatus>>({});
  const [lastProbedAt, setLastProbedAt] = useState<Date | null>(null);
  const [probeError, setProbeError] = useState<string | null>(null);
  const pendingRequestRef = useRef<string | null>(null);
  const probeTimerRef = useRef<number | null>(null);

  // Reset audit state whenever the user picks a different tour or
  // forces a reload — the previous probe results are no longer valid.
  useEffect(() => {
    setIframeReady(false);
    setProbeMap({});
    setLastProbedAt(null);
    setProbeError(null);
    pendingRequestRef.current = null;
    if (probeTimerRef.current !== null) {
      window.clearTimeout(probeTimerRef.current);
      probeTimerRef.current = null;
    }
  }, [selectedTourId, iframeKey]);

  // Listen for probe-result coming back from the iframe.
  useEffect(() => {
    function onMessage(e: MessageEvent<unknown>) {
      if (e.origin !== window.location.origin) return;
      if (
        !e.data ||
        typeof e.data !== "object" ||
        (e.data as { type?: unknown }).type !== "tour-preview:probe-result"
      ) {
        return;
      }
      const data = e.data as ProbeResultMessage;
      if (!pendingRequestRef.current || data.requestId !== pendingRequestRef.current) return;
      pendingRequestRef.current = null;
      if (probeTimerRef.current !== null) {
        window.clearTimeout(probeTimerRef.current);
        probeTimerRef.current = null;
      }
      setProbeMap((prev) => {
        const next = { ...prev };
        for (const r of data.results) next[r.stepId] = r.status;
        return next;
      });
      setLastProbedAt(new Date());
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  function runProbe() {
    if (!iframeRef.current?.contentWindow) return;
    if (steps.length === 0) return;
    const supported = steps.filter((s) => s.targetId || s.targetSelector);
    if (supported.length === 0) return;

    const requestId = `probe-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    pendingRequestRef.current = requestId;
    setProbeError(null);
    setProbeMap((prev) => {
      const next: Record<string, ProbeStatus> = { ...prev };
      for (const s of steps) {
        next[s.id] = s.targetId || s.targetSelector ? "probing" : "unsupported";
      }
      return next;
    });

    iframeRef.current.contentWindow.postMessage(
      {
        type: "tour-preview:probe-targets",
        requestId,
        targets: supported.map((s) => ({
          stepId: s.id,
          anchorId: s.targetId,
          selector: s.targetSelector,
        })),
      },
      window.location.origin,
    );

    probeTimerRef.current = window.setTimeout(() => {
      if (pendingRequestRef.current === requestId) {
        pendingRequestRef.current = null;
        setProbeError(
          "The preview iframe didn't respond within 5 seconds. Try reloading.",
        );
        setProbeMap((prev) => {
          const next = { ...prev };
          for (const s of supported) {
            if (next[s.id] === "probing") next[s.id] = "missing";
          }
          return next;
        });
      }
    }, PROBE_TIMEOUT_MS);
  }

  // Auto-probe once after the iframe loads + a short settle window
  // (gives lazy components a moment to mount).
  useEffect(() => {
    if (!iframeReady || steps.length === 0) return;
    const t = window.setTimeout(() => runProbe(), POST_LOAD_SETTLE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeReady, steps]);

  const tourSummary = useMemo(() => {
    const found = Object.values(probeMap).filter((s) => s === "found").length;
    const missing = Object.values(probeMap).filter((s) => s === "missing").length;
    const unsupported = Object.values(probeMap).filter((s) => s === "unsupported").length;
    return { found, missing, unsupported };
  }, [probeMap]);

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-4 max-w-7xl">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="icon" data-testid="link-audit-back">
            <Link href="/admin/tours">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-xl font-semibold" data-testid="text-audit-title">
              Tour audit
            </h1>
            <p className="text-sm text-muted-foreground">
              Pick a tour to load its page in a sandbox iframe and probe every
              step's target against the live DOM.
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tour</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {toursQuery.isLoading ? (
            <Skeleton className="h-9 w-full" />
          ) : (
            <Combobox<TourRecord>
              options={tours}
              value={selectedTourId}
              onChange={(key) => setSelectedTourId(key)}
              getKey={(t) => t.id}
              getLabel={(t) => `${t.routePattern} — ${t.title}`}
              searchKeys={(t) => [t.routePattern, t.title]}
              placeholder="Pick a tour to audit…"
              searchPlaceholder="Search by title or route…"
              emptyText="No tours found."
              triggerTestId="combobox-audit-tour"
              itemTestIdPrefix="combobox-audit-tour-item"
            />
          )}

          {selectedTour && (
            <div className="flex items-center justify-between gap-2 flex-wrap text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <code className="rounded bg-muted px-2 py-0.5 font-mono text-xs">
                  {selectedTour.routePattern}
                </code>
                {!previewable && (
                  <Badge variant="outline" className="gap-1">
                    <AlertTriangle className="h-3 w-3" /> Route is parametric — audit
                    not supported
                  </Badge>
                )}
                {lastProbedAt && (
                  <span className="text-xs text-muted-foreground" data-testid="text-audit-last-probed">
                    Last probed {lastProbedAt.toLocaleTimeString()}
                  </span>
                )}
              </div>
              {previewable && (
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setIframeKey((k) => k + 1)}
                    disabled={!iframeReady}
                    data-testid="button-audit-reload"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Reload &amp; re-probe
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="default"
                    onClick={runProbe}
                    disabled={!iframeReady || steps.length === 0}
                    data-testid="button-audit-probe"
                  >
                    Probe now
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedTour && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <Card className="min-h-[60vh]">
            <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
              <CardTitle className="text-base">Steps ({steps.length})</CardTitle>
              {Object.keys(probeMap).length > 0 && (
                <div className="flex items-center gap-1.5 text-xs">
                  <Badge variant="default" data-testid="badge-summary-found">
                    {tourSummary.found} found
                  </Badge>
                  {tourSummary.missing > 0 && (
                    <Badge variant="destructive" data-testid="badge-summary-missing">
                      {tourSummary.missing} missing
                    </Badge>
                  )}
                  {tourSummary.unsupported > 0 && (
                    <Badge variant="outline" data-testid="badge-summary-unsupported">
                      {tourSummary.unsupported} skipped
                    </Badge>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              {stepsQuery.isLoading ? (
                <>
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </>
              ) : steps.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center" data-testid="text-audit-no-steps">
                  This tour has no steps yet.
                </p>
              ) : (
                steps.map((s, idx) => (
                  <div
                    key={s.id}
                    className="flex items-start justify-between gap-2 rounded-md border p-2.5"
                    data-testid={`row-audit-step-${s.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-mono">#{idx + 1}</span>
                        <span className="truncate">{s.title || "(untitled)"}</span>
                      </div>
                      <code
                        className="mt-1 block truncate font-mono text-xs"
                        title={describeStepTarget(s)}
                      >
                        {describeStepTarget(s)}
                      </code>
                    </div>
                    <StatusBadge status={probeMap[s.id] ?? "pending"} />
                  </div>
                ))
              )}
              {probeError && (
                <p className="text-xs text-destructive" data-testid="text-audit-probe-error">
                  {probeError}
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="min-h-[60vh] flex flex-col">
            <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
              <CardTitle className="text-base">Live preview</CardTitle>
              {auditUrl && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => window.open(auditUrl, "_blank", "noopener")}
                  aria-label="Open preview in new tab"
                  data-testid="button-audit-open-new-tab"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              )}
            </CardHeader>
            <CardContent className="flex-1 p-0 border-t">
              {auditUrl ? (
                <iframe
                  key={iframeKey}
                  ref={iframeRef}
                  src={auditUrl}
                  title="Tour audit preview"
                  className="w-full h-full min-h-[55vh] border-0"
                  onLoad={() => setIframeReady(true)}
                  data-testid="iframe-audit-preview"
                />
              ) : (
                <div className="flex h-full min-h-[55vh] items-center justify-center text-sm text-muted-foreground p-6 text-center">
                  Audit isn't supported for this route — the path includes a
                  parameter or wildcard. Resolve targets manually.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
